import { mergeRefs } from '@react-aria/utils'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import cx from 'classnames'
import { forwardRef, useEffect, useRef } from 'react'

import type CommonTextInputProps from '@src/design-system/common/CommonTextInputProps'
import commonTextInputClasses from '@ui/shared/commonTextInputClasses'

import * as styles from './TextArea.css'

export interface TextAreaProps
  extends CommonTextInputProps,
    Omit<React.HTMLProps<HTMLTextAreaElement>, 'ref' | 'size' | 'label'> {
  /**
   * Sets whether the text area is resizable, and if so, in which directions.
   * It is ignored if `autoResize` is `true`
   *
   * @default none
   */
  resize?: 'none' | 'both' | 'horizontal' | 'vertical'

  /**
   * if `true`, the text area will automatically resize vertically when the text changes
   *
   * @default false
   */
  autoResize?: boolean
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      fullWidth,
      variant = 'outlined',
      size = 45,
      hasError = false,
      resize = 'none',
      rows = 1,
      autoResize = false,
      style,
      ...props
    },
    outerRef,
  ) => {
    const classes = commonTextInputClasses({ size, fullWidth, variant, hasError })
    const internalRef = useRef<HTMLTextAreaElement>(null)
    const ref = mergeRefs<HTMLTextAreaElement>(outerRef, internalRef)

    useEffect(() => {
      if (autoResize && internalRef.current) {
        internalRef.current.style.height = '1px'
        internalRef.current.style.height = `${internalRef.current.scrollHeight + 3}px`
      }
    })

    return (
      <textarea
        {...props}
        style={{
          ...style,
          ...assignInlineVars({
            [styles.paddingVar]: size === 45 ? '10px 16px' : '7px 16px',
            [styles.sizeVar]: `${size}px`,
            [styles.resizeVar]: resize,
          }),
        }}
        ref={ref}
        className={cx(...classes, styles.root({ autoResize }), className)}
        rows={rows}
      ></textarea>
    )
  },
)

export default TextArea
